import React, { useState, useEffect, useRef } from 'react';
import { initFaceAuth, verifyFace, generateTOTPCode, sendTOTPEmail } from '../utils/auth.js';

export default function VaultAuth({ onAuthenticated, onCancel }) {
  const [step, setStep] = useState('faceauth'); // faceauth, totp, complete
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [faceAuthStatus, setFaceAuthStatus] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const username = sessionStorage.getItem('jk_username');

  useEffect(() => {
    return () => {
      // Cleanup camera stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startFaceAuth = async () => {
    setFaceAuthStatus('Initializing camera...');
    const result = await initFaceAuth();

    if (!result.supported) {
      setFaceAuthStatus('Camera not available. Skipping face auth...');
      setTimeout(() => {
        moveToTOTP();
      }, 2000);
      return;
    }

    streamRef.current = result.stream;
    if (videoRef.current) {
      videoRef.current.srcObject = result.stream;
      videoRef.current.play();
    }

    setFaceAuthStatus(`Verify your face, ${username}...`);

    // Auto-verify after 2 seconds
    setTimeout(async () => {
      setFaceAuthStatus('Analyzing face...');
      const verifyResult = await verifyFace(videoRef.current, username);

      if (verifyResult.verified) {
        setFaceAuthStatus('✓ Face verified!');
        setTimeout(async () => {
          // Stop camera and move to TOTP step
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
          }
          moveToTOTP();
        }, 1000);
      } else {
        setFaceAuthStatus(`❌ ${verifyResult.message || 'Face not recognized'}. Please try again.`);
        setTimeout(() => {
          setFaceAuthStatus('');
        }, 3000);
      }
    }, 2000);
  };

  const moveToTOTP = async () => {
    setStep('totp');
    await sendEmailCode();
  };

  const sendEmailCode = async () => {
    setEmailStatus('Sending code to your email...');
    const result = await sendTOTPEmail(username);
    
    if (result.success) {
      setEmailStatus(`✓ Code sent to ${result.email}`);
      setEmailSent(true);
    } else {
      setEmailStatus(`❌ ${result.message}`);
    }
  };

  const handleTotpSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!totpCode) {
      setError('Please enter the 6-digit code');
      return;
    }

    // Generate the current valid TOTP code for this user
    const validCode = generateTOTPCode(`jk-${username}-2024`);
    
    if (totpCode === validCode) {
      completeAuth();
    } else {
      setError('Invalid code. Please check and try again.');
      setTotpCode('');
    }
  };

  const completeAuth = () => {
    sessionStorage.setItem('jk_vault_authenticated', 'true');
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setStep('complete');
    if (onAuthenticated) onAuthenticated();
  };

  const skipFaceAuth = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    moveToTOTP();
  };

  const handleCancel = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (onCancel) onCancel();
  };

  return (
    <div className="auth-gate" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2 style={{ color: '#c9a62b', margin: '8px 0' }}>🔐 Vault Access</h2>
            <p>Additional security required</p>
          </div>

          {step === 'faceauth' && (
            <div className="face-auth-container">
              <h3>Face Authentication</h3>
              <p className="face-auth-status">{faceAuthStatus}</p>

              <div className="video-container">
                <video
                  ref={videoRef}
                  width="320"
                  height="240"
                  style={{ borderRadius: 12, background: '#000' }}
                />
              </div>

              {username && (
                <div className="auth-user-info">
                  <p>Verifying: <strong>{username}</strong></p>
                </div>
              )}

              <div className="face-auth-actions">
                {!faceAuthStatus && (
                  <button className="btn rounded-full" onClick={startFaceAuth} style={{ borderRadius: '9999px' }}>
                    Start Face Verification
                  </button>
                )}
                <button className="btn rounded-full" onClick={skipFaceAuth} style={{ background: 'transparent', borderRadius: '9999px' }}>
                  Skip Face Auth
                </button>
                <button className="btn rounded-full" onClick={handleCancel} style={{ background: '#444', borderRadius: '9999px' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {step === 'totp' && (
            <div className="totp-container">
              <h3>Two-Factor Authentication</h3>
              <p className="totp-instruction">
                Check your email for the 6-digit code
              </p>

              {emailStatus && (
                <div className={`email-status ${emailSent ? 'success' : 'error'}`}>
                  {emailStatus}
                </div>
              )}

              <form onSubmit={handleTotpSubmit} className="auth-form">
                <div className="form-group">
                  <label>6-Digit Code</label>
                  <input
                    type="text"
                    value={totpCode}
                    onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength="6"
                    autoFocus
                    className="totp-input rounded-full"
                    style={{
                      fontSize: '24px',
                      letterSpacing: '8px',
                      textAlign: 'center',
                      fontFamily: 'monospace',
                      borderRadius: '9999px'
                    }}
                  />
                </div>

                {error && <div className="auth-error">{error}</div>}

                <button type="submit" className="btn auth-btn rounded-full" style={{ borderRadius: '9999px' }}>
                  Access Vault
                </button>

                <button 
                  type="button" 
                  className="resend-btn rounded-full"
                  onClick={sendEmailCode}
                  disabled={!emailSent}
                  style={{ borderRadius: '9999px' }}
                >
                  🔄 Resend Code
                </button>

                <button 
                  type="button" 
                  className="btn rounded-full"
                  onClick={handleCancel}
                  style={{ background: '#444', borderRadius: '9999px', marginTop: '12px' }}
                >
                  Cancel
                </button>

                <p className="totp-help">
                  💡 Didn't receive the email? Check your spam folder or click Resend Code
                </p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
